import { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { UploadCloud, FileSpreadsheet, CheckCircle2, AlertTriangle, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { createImportJob, commitImportJob, listImportJobs } from "@/lib/imports.functions";

const TARGET_FIELDS = [
  { key: "full_name", label: "Full name *", required: true },
  { key: "student_no", label: "Student no." },
  { key: "class_label", label: "Class / stream" },
  { key: "guardian_phone", label: "Guardian phone" },
  { key: "guardian_email", label: "Guardian email" },
  { key: "bus_route", label: "Bus route" },
  { key: "boarding", label: "Boarding? (yes/no)" },
];

export function ImportWizard({ schoolId }: { schoolId: string }) {
  const qc = useQueryClient();
  const [step, setStep] = useState<0 | 1 | 2 | 3>(0);
  const [filename, setFilename] = useState("");
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<Record<string, any>[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [jobId, setJobId] = useState<string | null>(null);
  const [pasteText, setPasteText] = useState("");

  const listJobs = useServerFn(listImportJobs);
  const jobs = useQuery<any[]>({ queryKey: ["import-jobs", schoolId], queryFn: () => listJobs({ data: { schoolId } }) });

  const create = useMutation({
    mutationFn: useServerFn(createImportJob),
    onSuccess: (r: any) => {
      setJobId(r.jobId); setStep(2);
      toast.success(`Staged ${r.rows} rows · ${r.errors} errors`);
      qc.invalidateQueries({ queryKey: ["import-jobs", schoolId] });
    },
    onError: (e: any) => toast.error(e.message),
  });
  const commit = useMutation({
    mutationFn: useServerFn(commitImportJob),
    onSuccess: (r: any) => {
      toast.success(`Inserted ${r.inserted} learners`); setStep(3);
      qc.invalidateQueries();
    },
    onError: (e: any) => toast.error(e.message),
  });

  const autoMap = (hdrs: string[]) => {
    const m: Record<string, string> = {};
    TARGET_FIELDS.forEach(t => {
      const guess = hdrs.find(h => h.toLowerCase().replace(/[^a-z]/g, "").includes(t.key.replace(/_/g, "")));
      if (guess) m[t.key] = guess;
    });
    return m;
  };

  const handleFile = async (file: File) => {
    setFilename(file.name);
    if (/\.csv$/i.test(file.name)) {
      const text = await file.text();
      const parsed = Papa.parse(text, { header: true, skipEmptyLines: true });
      const data = parsed.data as Record<string, any>[];
      const hdrs = parsed.meta.fields ?? Object.keys(data[0] ?? {});
      setHeaders(hdrs); setRows(data); setMapping(autoMap(hdrs)); setStep(1);
    } else {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json<Record<string, any>>(ws, { defval: "" });
      const hdrs = Object.keys(data[0] ?? {});
      setHeaders(hdrs); setRows(data); setMapping(autoMap(hdrs)); setStep(1);
    }
  };

  const handlePaste = () => {
    const parsed = Papa.parse(pasteText.trim(), { header: true, skipEmptyLines: true });
    const data = parsed.data as Record<string, any>[];
    const hdrs = parsed.meta.fields ?? Object.keys(data[0] ?? {});
    if (!hdrs.length) return toast.error("Need a header row");
    setFilename("pasted.csv"); setHeaders(hdrs); setRows(data); setMapping(autoMap(hdrs)); setStep(1);
  };

  const preview = useMemo(() => rows.slice(0, 8), [rows]);

  return (
    <div className="space-y-4">
      <Card className="relative overflow-hidden bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900 p-6 text-emerald-50">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-emerald-200/70">Data import</div>
            <div className="mt-1 font-display text-2xl">Bring learners in from CSV, Excel or paste</div>
            <div className="mt-1 text-sm text-emerald-100/80">Auto-detects headers, previews errors before commit, and posts each row to your audit trail.</div>
          </div>
          <div className="flex items-center gap-2 text-xs">
            {["Upload","Map","Validate","Commit"].map((s, i) => (
              <div key={s} className={`flex items-center gap-1 rounded-full px-3 py-1 ${i === step ? "bg-emerald-50 text-emerald-900" : "border border-white/20"}`}>
                <span className="font-semibold">{i + 1}</span> {s}
              </div>
            ))}
          </div>
        </div>
      </Card>

      {step === 0 && (
        <div className="grid gap-4 md:grid-cols-2">
          <Card className="p-6">
            <div className="grid place-items-center rounded-xl border-2 border-dashed p-8 text-center">
              <FileSpreadsheet className="h-10 w-10 text-emerald-700" />
              <div className="mt-2 font-display text-lg">Drop a CSV or XLSX</div>
              <div className="text-xs text-muted-foreground">Up to 5,000 rows per file. Sample columns: name, class, phone, email, route</div>
              <label className="mt-3 inline-flex cursor-pointer">
                <input type="file" hidden accept=".csv,.xlsx,.xls" onChange={e => e.target.files && handleFile(e.target.files[0])} />
                <span className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground"><UploadCloud className="h-4 w-4" />Choose file</span>
              </label>
            </div>
          </Card>
          <Card className="p-6">
            <div className="mb-2 font-display text-lg">Or paste rows</div>
            <div className="text-xs text-muted-foreground">Copy directly from a spreadsheet — first row must be headers.</div>
            <Textarea rows={8} value={pasteText} onChange={e => setPasteText(e.target.value)} className="mt-2 font-mono text-xs" placeholder="full_name,class_label,guardian_phone\nAmina Nakato,P4,+256772000001" />
            <Button className="mt-2" onClick={handlePaste} disabled={!pasteText.trim()}>Parse pasted rows</Button>
          </Card>
        </div>
      )}

      {step >= 1 && (
        <Card className="p-4">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <div className="font-display text-lg">{filename}</div>
              <div className="text-xs text-muted-foreground">{rows.length} rows · {headers.length} columns detected</div>
            </div>
            <Badge variant="secondary" className="capitalize">Step {step + 1} · {["upload","map","validate","done"][step]}</Badge>
          </div>

          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {TARGET_FIELDS.map(t => (
              <div key={t.key} className="rounded-md border p-3">
                <div className="text-xs font-semibold">{t.label}</div>
                <Select value={mapping[t.key] ?? "__none__"} onValueChange={v => setMapping({ ...mapping, [t.key]: v === "__none__" ? "" : v })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Choose column" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— skip —</SelectItem>
                    {headers.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>

          <div className="mt-4 overflow-auto rounded-md border">
            <table className="min-w-full text-xs">
              <thead className="bg-muted/50"><tr>{TARGET_FIELDS.map(t => <th key={t.key} className="px-2 py-1 text-left font-semibold">{t.label}</th>)}</tr></thead>
              <tbody>
                {preview.map((r, i) => (
                  <tr key={i} className="border-t">
                    {TARGET_FIELDS.map(t => <td key={t.key} className="px-2 py-1">{String(r[mapping[t.key]] ?? "")}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex justify-between">
            <Button variant="outline" onClick={() => { setStep(0); setRows([]); setJobId(null); }}>Start over</Button>
            <div className="flex gap-2">
              {step === 1 && (
                <Button disabled={!mapping.full_name || create.isPending} onClick={() => create.mutate({ data: { schoolId, kind: "students", filename, rows, mapping } })}>
                  <ArrowRight className="mr-1 h-3 w-3" />Validate & stage
                </Button>
              )}
              {step === 2 && jobId && (
                <Button disabled={commit.isPending} onClick={() => commit.mutate({ data: { jobId } })}>
                  <CheckCircle2 className="mr-1 h-3 w-3" />Commit to school
                </Button>
              )}
              {step === 3 && (
                <Button variant="secondary" onClick={() => { setStep(0); setRows([]); setJobId(null); }}>Import another batch</Button>
              )}
            </div>
          </div>
        </Card>
      )}

      <Card className="p-3">
        <div className="mb-2 flex items-center justify-between">
          <div className="font-display text-sm">Recent import jobs</div>
          <Badge variant="outline" className="text-[10px]">retained 10y</Badge>
        </div>
        <div className="space-y-1">
          {(jobs.data ?? []).map((j: any) => (
            <div key={j.id} className="grid grid-cols-5 items-center gap-2 rounded-md border p-2 text-xs">
              <div className="font-medium">{j.filename}</div>
              <div className="text-muted-foreground">{j.kind}</div>
              <div>{j.rows_total} rows</div>
              <div>{j.rows_ok ?? 0} ok · {j.rows_error ?? 0} err</div>
              <Badge variant={j.status === "committed" ? "default" : "outline"} className="justify-self-end capitalize">{j.status}</Badge>
            </div>
          ))}
          {!(jobs.data ?? []).length && <div className="p-4 text-center text-xs text-muted-foreground">No imports yet</div>}
        </div>
      </Card>

      {step === 3 && (
        <Card className="border-emerald-200 bg-emerald-50 p-4 text-emerald-900">
          <div className="flex items-center gap-2"><Sparkles className="h-4 w-4" /><div className="font-medium">Import committed</div></div>
          <div className="mt-1 text-sm">All rows are now visible in Students &amp; Guardians. Each row wrote to the compliance audit trail.</div>
        </Card>
      )}
    </div>
  );
}

